import React, { useState } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
} from '@material-ui/core'

interface AddLineItemDialogProps {
    open: boolean
    onClose: () => void
    onAddLineItem: (data: {
        description: string
        subtotal?: string
        quantity?: string
        unit_price?: string
    }) => Promise<void>
    isLoading?: boolean
}

export const AddLineItemDialog: React.FC<AddLineItemDialogProps> = ({
    open,
    onClose,
    onAddLineItem,
    isLoading = false,
}) => {
    const [formData, setFormData] = useState({
        description: '',
        subtotal: '',
        quantity: '',
        unit_price: '',
    })

    const [errors, setErrors] = useState<{ [key: string]: string }>({})

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }))
        }
    }

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {}

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required'
        }

        // Validate that either subtotal is provided OR both quantity and unit_price are provided
        const hasSubtotal = formData.subtotal.trim() !== ''
        const hasQuantity = formData.quantity.trim() !== ''
        const hasUnitPrice = formData.unit_price.trim() !== ''

        if (!hasSubtotal && !(hasQuantity && hasUnitPrice)) {
            newErrors.subtotal =
                'Either subtotal must be provided, or both quantity and unit_price must be provided'
            newErrors.quantity =
                'Either subtotal must be provided, or both quantity and unit_price must be provided'
            newErrors.unit_price =
                'Either subtotal must be provided, or both quantity and unit_price must be provided'
        }

        // Validate numeric fields
        if (formData.subtotal && isNaN(parseFloat(formData.subtotal))) {
            newErrors.subtotal = 'Subtotal must be a valid number'
        }
        if (formData.quantity && isNaN(parseFloat(formData.quantity))) {
            newErrors.quantity = 'Quantity must be a valid number'
        }
        if (formData.unit_price && isNaN(parseFloat(formData.unit_price))) {
            newErrors.unit_price = 'Unit price must be a valid number'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validateForm()) {
            return
        }

        try {
            await onAddLineItem({
                description: formData.description,
                subtotal: formData.subtotal || undefined,
                quantity: formData.quantity || undefined,
                unit_price: formData.unit_price || undefined,
            })

            // Reset form
            setFormData({
                description: '',
                subtotal: '',
                quantity: '',
                unit_price: '',
            })
            setErrors({})
        } catch (error) {
            console.error('Error adding line item:', error)
        }
    }

    const handleClose = () => {
        setFormData({
            description: '',
            subtotal: '',
            quantity: '',
            unit_price: '',
        })
        setErrors({})
        onClose()
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Line Item</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} style={{ marginTop: 8 }}>
                    {/* Description */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) =>
                                handleInputChange('description', e.target.value)
                            }
                            error={!!errors.description}
                            helperText={errors.description}
                            required
                        />
                    </Grid>

                    {/* Subtotal */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Subtotal"
                            type="number"
                            value={formData.subtotal}
                            onChange={(e) =>
                                handleInputChange('subtotal', e.target.value)
                            }
                            error={!!errors.subtotal}
                            helperText={
                                errors.subtotal ||
                                'Enter subtotal OR quantity + unit price'
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>

                    {/* Quantity and Unit Price */}
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Quantity"
                            type="number"
                            value={formData.quantity}
                            onChange={(e) =>
                                handleInputChange('quantity', e.target.value)
                            }
                            error={!!errors.quantity}
                            helperText={errors.quantity}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Unit Price"
                            type="number"
                            value={formData.unit_price}
                            onChange={(e) =>
                                handleInputChange('unit_price', e.target.value)
                            }
                            error={!!errors.unit_price}
                            helperText={errors.unit_price}
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>

                    {/* Helper Text */}
                    <Grid item xs={12}>
                        <Box mt={1}>
                            <Typography variant="caption" color="textSecondary">
                                <strong>Note:</strong> You can either enter a
                                subtotal directly, or enter both quantity and
                                unit price. If you enter quantity and unit
                                price, the subtotal will be calculated
                                automatically.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={isLoading}
                >
                    {isLoading ? 'Adding...' : 'Add Line Item'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
